import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Minus, Plus, X, Send } from "lucide-react";
import { api, formatMoney, formatApiError } from "../lib/api";
import PriceDisplay from "./PriceDisplay";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

/**
 * Lets a renter propose a TOTAL price (not per-day) for a car BEFORE confirming the booking,
 * for the currently selected pickup/return dates. The floor is shown explicitly so renters
 * know exactly how low they can go — deliberately transparent rather than hidden.
 */
export default function NegotiateDialog({ open, onOpenChange, car, dates, times, onSuccess, isGuest = false }) {
  const { currencies } = useCurrency();
  const days = React.useMemo(() => {
    if (!dates?.start || !dates?.end) return 1;
    const ms = new Date(dates.end).getTime() - new Date(dates.start).getTime();
    return Math.max(1, Math.round(ms / 86400000));
  }, [dates]);
  const listing = Number(car?.price_per_day || 0) * days;
  const step = Math.max(500, Math.round(listing * 0.05));
  const [bid, setBid] = useState(listing);
  const [floor, setFloor] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  React.useEffect(() => {
    if (open) {
      setBid(listing);
      if (car?.id) {
        api.get("/negotiations/floor", { params: { car_id: car.id, days } })
          .then(({ data }) => setFloor(data.floor_amount))
          .catch(() => setFloor(0));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listing, car?.id, days]);

  const dec = () => setBid((b) => Math.max(floor || step, b - step));
  const inc = () => setBid((b) => b + step);

  const submit = async () => {
    if (!bid || bid <= 0) {
      toast.error("Enter a valid offer.");
      return;
    }
    if (!dates?.start || !dates?.end) {
      toast.error("Please select pickup and return dates first.");
      return;
    }
    if (isGuest && (!guestFirstName || !guestLastName || !guestEmail || !guestPhone)) {
      toast.error("Please fill in all your details first.");
      return;
    }
    setSubmitting(true);
    try {
      let data;
      if (isGuest) {
        const res = await api.post("/guest/negotiate", {
          car_id: car.id,
          proposed_price: bid,
          start_date: dates.start,
          end_date: dates.end,
          start_time: times?.start,
          end_time: times?.end,
          first_name: guestFirstName,
          last_name: guestLastName,
          email: guestEmail,
          phone: guestPhone,
        });
        data = res.data;
        toast.success("Offer sent! The owner will respond within 24 hours. Check your email for updates.");
      } else {
        const res = await api.post("/negotiations", {
          car_id: car.id,
          proposed_price: bid,
          start_date: dates.start,
          end_date: dates.end,
          start_time: times?.start,
          end_time: times?.end,
        });
        data = res.data;
        toast.success("Offer sent. We'll notify you when the owner responds.");
      }
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const pctOff = listing > 0 ? Math.round(((listing - bid) / listing) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="negotiate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Negotiate the Price</DialogTitle>
          <DialogDescription>
            Adjust your total offer for <strong>{car?.title}</strong> for {days} day{days > 1 ? "s" : ""}. The owner
            has 24 hours to accept, reject, or counter, with a limited number of rounds before the offer auto-closes.
          </DialogDescription>
        </DialogHeader>

        {isGuest && (
          <div className="space-y-3 mb-4 p-4 bg-muted/30 rounded-xl border border-border">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Your details</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">First name</Label>
                <Input value={guestFirstName} onChange={e => setGuestFirstName(e.target.value)} placeholder="Jean" className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Last name</Label>
                <Input value={guestLastName} onChange={e => setGuestLastName(e.target.value)} placeholder="Bosco" className="mt-1 h-9 text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="you@example.com" className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone</Label>
              <Input value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="+250 78x xxx xxx" className="mt-1 h-9 text-sm" />
            </div>
          </div>
        )}
        <div className="rounded-2xl bg-muted/40 border border-border p-5 my-2 text-center">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
            Your total offer ({days} day{days > 1 ? "s" : ""})
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={dec}
              disabled={submitting || (floor > 0 && bid <= floor)}
              data-testid="negotiate-dec"
              className="w-11 h-11 rounded-full bg-white border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40"
            >
              <Minus size={18} />
            </button>
            <div className="min-w-[160px] font-display text-3xl font-bold tracking-tight" data-testid="negotiate-bid">
              <PriceDisplay amount={bid} listingCurrency={car?.currency} />
            </div>
            <button
              type="button"
              onClick={inc}
              disabled={submitting}
              data-testid="negotiate-inc"
              className="w-11 h-11 rounded-full bg-[#2A9D8F] text-white flex items-center justify-center hover:bg-[#23887A] transition-colors disabled:opacity-40"
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Listed total: <PriceDisplay amount={listing} listingCurrency={car?.currency} />
            {pctOff !== 0 && ` · ${pctOff > 0 ? `You save ${pctOff}%` : `${Math.abs(pctOff)}% above listing`}`}
          </div>
          {floor > 0 && (
            <div className="mt-1 text-xs text-muted-foreground" data-testid="negotiate-floor">
              Minimum offer: <PriceDisplay amount={floor} listingCurrency={car?.currency} />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="negotiate-cancel"
          >
            <X size={14} className="mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="negotiate-submit"
            className="bg-[#E97A48] hover:bg-[#D86A38] text-white"
          >
            {submitting ? "Sending…" : <><Send size={14} className="mr-1.5" /> Send offer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
